import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Typography,
  Stack,
  styled,
  SxProps,
  Popper,
  FormControl,
  OutlinedInput,
  ClickAwayListener,
  Fade,
} from "@mui/material";
import SearchResult from "../SearchResult/search-result";

import SearchIcon from "@mui/icons-material/Search";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { GetTitleRouterPath, IsStringAnId } from "../../../Utils/helpers";
import { cacheService, searchService, userService } from "../../../Core/Api/services";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { throttle } from "throttle-debounce";
import { useNavigate } from "react-router";
import { routePaths } from "../../../Router/router";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { hexToRgbString } from "../../../Utils/Helpers/cssUtils";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { useAppDispatch } from "../../../Redux/redux-store";
import { SetSearchPattern } from "../../../Redux/Reducers/redux-slice-filters";
import { useTranslation } from "react-i18next";

type AutocompleteCategoryProps = {
  title: string;
  assets: DbAssetObjectInfos[];
  metadataList: DbMetadataFieldInfos[];
  loading: boolean;
  onSearchResultClick: () => void;
  isDrive?: boolean;
};

export interface FilterTags {
  tag: string;
  count: number;
}

type AutocompleteFilterProps = {
  sx?: SxProps;
  tags: FilterTags[];
  activeTags: string[];
  onClick: (tag: string) => void;
};

export const AutocompleteCategory: React.FC<AutocompleteCategoryProps> = (props) => {
  const { title, assets, loading, onSearchResultClick, isDrive = false } = props;
  const { t } = useTranslation();

  return (
    <Box key={title} sx={{ mb: 2 }}>
      <Typography variant="h6" color="text.tier" textTransform="uppercase" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Stack spacing={0.5}>
        {assets.length > 0 &&
          assets.map((option) => (
            <SearchResult
              key={option.id}
              asset={option}
              metadataList={props.metadataList}
              to={`${isDrive ? routePaths.drive.path : GetTitleRouterPath()}/${option.id}`}
              onClick={onSearchResultClick}
            />
          ))}
        {assets.length === 0 && !loading && <Typography>{t("alert:info.noSearchResult")}</Typography>}
        {loading && <Typography>{t("button:searching")}</Typography>}
      </Stack>
    </Box>
  );
};

export const AutocompleteFilter: React.FC<AutocompleteFilterProps> = (props) => {
  const { tags, activeTags, onClick, sx = {} } = props;
  return (
    <Stack direction="row" spacing={0.5} sx={sx}>
      {tags.map((tag) => {
        const isActive = activeTags.indexOf(tag.tag) >= 0;
        return (
          <Chip
            label={
              <>
                {tag.tag}{" "}
                <Typography component="span" variant="body2" color={`${isActive ? "" : "text.primary"}`}>
                  ({tag.count})
                </Typography>
              </>
            }
            className={isActive ? "active" : ""}
            onClick={() => onClick(tag.tag)}
            key={tag.tag}
            size="large"
            variant="tag"
          />
        );
      })}
    </Stack>
  );
};

export type AutocompleteSearchProps = {
  sx?: SxProps;

  isSearchBarVisible?: boolean;
};

const AutocompleteSearch = React.forwardRef<HTMLDivElement | null, AutocompleteSearchProps>((props, ref) => {
  const { t } = useTranslation();
  const { isSearchBarVisible = true, ...otherProps } = props;
  const $searchRef = React.useRef<HTMLInputElement>(undefined);
  const [popperOpen, setPopperOpen] = React.useState(false);
  const [tagActive, setTagActive] = React.useState<string>("");
  const [searchValue, setSearchValue] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [searchFieldWidth, setSearchFieldWidth] = React.useState(0);
  const [popperAnchorEl, setPopperAnchorEl] = React.useState<HTMLInputElement>();

  const [searchHistory, setSearchHistory] = React.useState<DbAssetObjectInfos[]>([]);
  const [search, setSearch] = React.useState<DbAssetObjectInfos[]>([]);
  const [tagsGenres, setTagsGenres] = React.useState<FilterTags[]>([]);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const maxTagsNumber = 5;
  const metadataList = useMemo(() => cacheService.GetMetadata(), []);
  const platformMode = cacheService.GetPlatformMode();

  const handleSetTagFilter = (tag: string) => {
    if (tagActive === tag) setTagActive("");
    else setTagActive(tag);
  };

  const throttledTypeheadSearch = useMemo(
    () =>
      throttle(
        400, // Wait half sec
        (searchValue: string) => {
          let request: SearchRequestNative = new SearchRequestNative({
            hideTitleWithoutAsset: false,
            onlyConfidential: false,
            pattern: searchValue,
            outputLayer: ObjectLayer.Title,
            limit: 10, // 10 latest
          });

          setLoading(true);

          searchService.SearchBasic(request).then((response) => {
            if (response.results) setSearch(response.results);

            // Compute tags
            let tags: FilterTags[] = [];

            response.results?.forEach((asset) => {
              let genres = GetMetadataByTagName(asset, "titleGenres", metadataList)?.value;

              if (genres) {
                let array = genres as string[];

                array.forEach((element) => {
                  let index = tags.findIndex((x) => x.tag === element);

                  if (index !== -1) tags[index].count++;
                  else tags.push({ tag: element, count: 1 });
                });
              }
            });

            setTagsGenres(tags);
            setLoading(false);
          });
        },
        {
          noLeading: true,
        }
      ),
    []
  );

  const onFocusInput = React.useCallback(() => {
    setPopperOpen(true);
  }, []);

  const onClickAway = React.useCallback(() => {
    setPopperOpen(false);
  }, []);

  const onChangeInput: React.ChangeEventHandler<HTMLInputElement> = React.useCallback((e) => {
    setSearchValue(e.target.value);

    if (e.target.value === "") setSearch([]);
    else if (e.target.value !== "" && e.target.value !== "*" && e.target.value.length >= 3)
      throttledTypeheadSearch(e.target.value);
  }, []);

  const isAssetIdSearch = useMemo((): boolean => IsStringAnId(searchValue), [searchValue]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (!isAssetIdSearch) {
        if (platformMode === PlatformMode.Distribution) navigate(`${routePaths.catalog.path}/find/${searchValue}`);
        else {
          // Consider Drive search
          dispatch(SetSearchPattern(searchValue));
          navigate(routePaths.drive.path);
        }
      } else {
        navigate(`${routePaths.asset.path}/${searchValue}`);
      }
    }
  };

  React.useEffect(() => {
    if (!$searchRef.current) return;
    setSearchFieldWidth($searchRef.current.clientWidth);
    setPopperAnchorEl($searchRef.current);

    userService.GetSearchHistory().then(function (response) {
      if (response.searchHistory !== null) {
        let objectIds = response.searchHistory
          .filter((x) => x.layer === ObjectLayer.Title)
          .map((asset) => asset.objectId!);

        let request: SearchRequestNative = new SearchRequestNative({
          hideTitleWithoutAsset: false,
          onlyConfidential: false,
          outputLayer: ObjectLayer.Title,
          titleIds: objectIds,
          limit: 10, // 10 latest
        });

        let orderedIds = response.searchHistory.map((x) => x.objectId).filter((item) => item !== null);
        searchService.SearchBasic(request).then((response) => {
          if (response.results)
            setSearchHistory(
              response.results.sort((a, b) => {
                return orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id);
              })
            );
        });
      }
    });
  }, []);

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <StyledSearch ref={ref} isVisible={isSearchBarVisible}>
        <FormControl size="large" sx={{ width: "100%" }}>
          <StyledOutlinedInput
            placeholder={
              platformMode === PlatformMode.Adam
                ? "Rechercher des assets par titre..."
                : t("field:placeholder.searchTitleOrCast")
            }
            defaultValue=""
            id="search-autocomplete"
            ref={$searchRef}
            onFocus={onFocusInput}
            onChange={onChangeInput}
            onKeyDown={handleKeyDown}
            endAdornment={<SearchIcon />}
            size="large"
          />
        </FormControl>
        <Popper
          open={popperOpen}
          disablePortal={true}
          placement="bottom-start"
          {...(popperAnchorEl ? { anchorEl: popperAnchorEl } : {})}
          sx={{ zIndex: 4 }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <StyledBox {...otherProps} width={searchFieldWidth}>
                <AutocompleteFilter
                  tags={tagsGenres.slice(0, maxTagsNumber)}
                  activeTags={[tagActive]}
                  onClick={(activeTag) => handleSetTagFilter(activeTag)}
                  sx={{ mb: 2 }}
                />
                <StyledWrapper>
                  <AutocompleteCategory
                    title={platformMode === PlatformMode.Adam ? t("common:assets") : t("common:titles")}
                    assets={(searchValue !== "" ? search : searchHistory).filter(
                      (x) =>
                        tagActive === "" ||
                        GetMetadataByTagName(x, "titleGenres", metadataList)?.value?.includes(tagActive)
                    )}
                    metadataList={metadataList}
                    key="category-titles"
                    onSearchResultClick={() => false}
                    loading={loading}
                  />
                </StyledWrapper>
              </StyledBox>
            </Fade>
          )}
        </Popper>
      </StyledSearch>
    </ClickAwayListener>
  );
});

export default AutocompleteSearch;

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "sx" && prop !== "width",
})<{ width: number }>(({ theme, width }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: 12,
  width: width > 0 ? `${width}px` : "100%",
  height: 320,
  borderRadius: 12,
  background: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  overflow: "hidden",
  padding: theme.spacing(1.5),
  boxShadow: theme.shadows[6],
}));

const StyledWrapper = styled(Box)(({ theme }) => ({
  overflow: "auto",
  ...theme.helpers(theme).scrollBar,
}));
const StyledSearch = styled(Box, { shouldForwardProp: (prop) => prop !== "isVisible" })<{ isVisible: boolean }>(
  ({ theme, isVisible }) => ({
    position: "relative",
    zIndex: 2,
    transition: "opacity .6s cubic-bezier(0.22, 1, 0.36, 1), background .3s",
    willChange: "opacity",
    borderRadius: 8,

    ...(!isVisible && {
      opacity: 0,
      pointerEvents: "none",
    }),
    ".MuiInputBase-root": {
      color: theme.palette.text.primary,
      transition: "background .3s",
      background: `rgba(${hexToRgbString(theme.palette.background.paper)}, .4)`,

      "&:hover": {
        background: theme.palette.backgroundAlpha.hover,
      },

      "&.Mui-focused": {
        ".MuiOutlinedInput-notchedOutline": {
          borderWidth: 1,
        },
        "&:hover": {
          background: theme.palette.backgroundAlpha.main,
        },
      },
    },
    ".MuiInputBase-input::placeholder, .MuiInputBase-input::-webkit-input-placeholder": {
      color: theme.palette.text.primary,
    },
    ".MuiSvgIcon-root": {
      color: theme.palette.text.secondary,
    },
  })
);

const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  fieldset: {
    border: `1px solid ${theme.palette.backgroundAlpha.hover}`,
  },
}));
